// src/utils/qrBulkUtils.ts
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { QRCodeOptions, renderQRCodeToCanvas } from './qrCodeUtils';

// src/utils/qrBulkTypes.ts

export type ProgressStage =
    | 'INIT'
    | 'RENDER_QR'
    | 'COMPOSE_PAGE'
    | 'ZIP'
    | 'DONE';

export type BulkProgress = {
    current: number;
    total: number;
    percent: number;
    stage: ProgressStage;
};

export type A4LayoutConfig = {
    enabled: boolean;

    columns: number;

    rows: number;

    pagePadding?: number;

    /** Horizontal gap between QRs */
    colGap?: number;

    /** Vertical gap between QRs (BOTTOM GAP FIX) */
    rowGap?: number;

    /** DPI for print (300 recommended) */
    dpi?: number;
};

/**
 * Format date-time for filenames
 * Example: 2025-01-15_14-32-10
 */
function getDateTimeString(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');

    return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_` +
        `${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`
    );
}

export type BulkQRItem = {
    /** Unique file name (without extension) */
    fileName: string;
    /** Text to encode in QR */
    value: string;
    /** Optional per-item label override */
    labelLines?: string[];
};

/**
 * Generate multiple QRs and download as ZIP
 */
export async function generateBulkQRsAsZip(
    items: BulkQRItem[],
    options: QRCodeOptions,
    zipBaseName = 'EV_QR_CODES',
): Promise<void> {
    if (!items || items.length === 0) {
        throw new Error('No QR items provided');
    }

    const zip = new JSZip();

    for (const item of items) {
        const canvas = await renderQRCodeToCanvas(item.value, {
            ...options,
            label: item.labelLines
                ? {
                      ...(options.label ?? {}),
                      lines: item?.labelLines,
                  }
                : options?.label,
        });

        const blob: Blob | null = await new Promise((resolve) =>
            canvas.toBlob((b) => resolve(b), 'image/png'),
        );

        if (blob) {
            zip.file(`${item.fileName}.png`, blob);
        }
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });

    const zipFileName = `${zipBaseName}_${getDateTimeString()}.zip`;
    saveAs(zipBlob, zipFileName);
}

export function yieldToBrowser(): Promise<void> {
    return new Promise((resolve) => {
        // requestIdleCallback if available, fallback to setTimeout
        if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(() => resolve());
        } else {
            setTimeout(resolve, 0);
        }
    });
}

/**
 * A4 canvas size at given DPI
 */
export function getA4CanvasSize(dpi = 300) {
    // A4 in inches: 8.27 × 11.69
    return {
        width: Math.round(8.27 * dpi),
        height: Math.round(11.69 * dpi),
    };
}

export async function generateBulkQRs(
    items: BulkQRItem[],
    qrOptions: QRCodeOptions,
    config?: {
        zipBaseName?: string;
        onProgress?: (p: BulkProgress) => void;
        a4Layout?: A4LayoutConfig;
    },
): Promise<void> {
    if (!items || items?.length === 0) {
        throw new Error('No QR items provided');
    }
    const { zipBaseName = 'QR_CODES', onProgress, a4Layout } = config || {};

    const scale = qrOptions.scale ?? 2;
    const zip = new JSZip();
    let filesAdded = 0;

    onProgress?.({
        current: 0,
        total: items.length,
        percent: 0,
        stage: 'INIT',
    });

    /* =====================================================
       MODE 1: A4 ENABLED (STICKER SHEETS)
       ===================================================== */
    if (a4Layout?.enabled) {
        const {
            columns,
            rows,
            pagePadding = 40,
            colGap = 20,
            rowGap = 50,
            dpi = 300,
        } = a4Layout;

        const { width: pageW, height: pageH } = getA4CanvasSize(dpi);
        const perPage = columns * rows;
        const totalPages = Math.ceil(items?.length / perPage);

        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
            onProgress?.({
                current: pageIndex + 1,
                total: totalPages,
                percent: Math.round(((pageIndex + 1) / totalPages) * 100),
                stage: 'COMPOSE_PAGE',
            });

            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = pageW;
            pageCanvas.height = pageH;

            const ctx = pageCanvas.getContext('2d');
            if (!ctx) continue;

            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, pageW, pageH);

            const usableW = pageW - pagePadding * 2;
            const usableH = pageH - pagePadding * 2;

            const cellW = (usableW - colGap * (columns - 1)) / columns;
            const cellH = (usableH - rowGap * (rows - 1)) / rows;

            const start = pageIndex * perPage;
            const slice = items.slice(start, start + perPage);

            for (let i = 0; i < slice?.length; i++) {
                const item = slice[i];
                const row = Math.floor(i / columns);
                const col = i % columns;

                const qrLogicalSize = Math.floor(
                    Math.min(cellW, cellH) / scale,
                );
                if (qrLogicalSize <= 0) continue;

                const qrCanvas = await renderQRCodeToCanvas(item.value, {
                    ...qrOptions,
                    scale,
                    logicalSize: qrLogicalSize,
                    label: item.labelLines
                        ? { ...(qrOptions.label ?? {}), lines: item.labelLines }
                        : qrOptions.label,
                });

                const x =
                    pagePadding +
                    col * (cellW + colGap) +
                    (cellW - qrCanvas.width) / 2;

                const y =
                    pagePadding +
                    row * (cellH + rowGap) +
                    (cellH - qrCanvas.height) / 2;

                ctx.drawImage(qrCanvas, x, y);
            }

            const blob = await new Promise<Blob | null>((r) =>
                pageCanvas.toBlob((b) => r(b), 'image/png'),
            );

            if (blob) {
                zip.file(`page_${pageIndex + 1}.png`, blob);
                filesAdded++;
            }
        }
    } else {
        /* =====================================================
       MODE 2: A4 DISABLED (INDIVIDUAL PNGs)
       ===================================================== */
        const total = items.length;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            onProgress?.({
                current: i + 1,
                total,
                percent: Math.round(((i + 1) / total) * 100),
                stage: 'RENDER_QR',
            });

            const canvas = await renderQRCodeToCanvas(item.value, {
                ...qrOptions,
                scale,
                label: item.labelLines
                    ? { ...(qrOptions.label ?? {}), lines: item.labelLines }
                    : qrOptions.label,
            });

            const blob = await new Promise<Blob | null>((r) =>
                canvas.toBlob((b) => r(b), 'image/png'),
            );

            if (blob) {
                zip.file(`${item.fileName}.png`, blob);
                filesAdded++;
            }
        }
    }

    /* =====================================================
       FINAL ZIP
       ===================================================== */
    if (filesAdded === 0) {
        throw new Error('No QR images were generated. ZIP aborted.');
    }

    onProgress?.({
        current: filesAdded,
        total: filesAdded,
        percent: 100,
        stage: 'ZIP',
    });

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `${zipBaseName}_${getDateTimeString()}.zip`);

    onProgress?.({
        current: filesAdded,
        total: filesAdded,
        percent: 100,
        stage: 'DONE',
    });
}
